import { useMemo, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  fetchZoneOptions,
  hasAccess,
  type AdCampaign,
  type GuestAccessResult,
  type IdentificationMethod,
  type OtpIssued,
  type ZoneOptions,
} from '@/features/portal/api'
import type { ActivatedPackage, AccountVoucherOutcome, RouterLogin } from '@/features/portal/accountVoucher'
import { clearPendingVoucher, readPendingVoucher } from '@/features/portal/pendingVoucher'
import type { VoucherCard } from '@/features/portal/voucherCard'
import { translatePortalError } from '@/shared/api/errors'
import { looksLikeMac } from '@/shared/lib/mac'
import { parsePortalParams, type PortalParams } from '@/shared/lib/portalParams'
import { sessionStore } from '@/shared/lib/sessionStore'
import { ErrorResult } from '@/shared/ui/ErrorResult'
import { LoadingScreen } from '@/shared/ui/LoadingScreen'
import { PortalShell } from '@/shared/ui/PortalShell'
import { ConfigErrorPage } from './ConfigErrorPage'
import { AccountActivatedStep } from './steps/AccountActivatedStep'
import { AccountVoucherStep } from './steps/AccountVoucherStep'
import { AdChooseStep } from './steps/AdChooseStep'
import { AdWatchStep } from './steps/AdWatchStep'
import { EntryStep } from './steps/EntryStep'
import { SmsCodeStep } from './steps/SmsCodeStep'
import { SmsPhoneStep } from './steps/SmsPhoneStep'
import { SuccessStep } from './steps/SuccessStep'
import { VoucherStep } from './steps/VoucherStep'

type Step =
  | { kind: 'blockedNoAds' }
  | { kind: 'chooseCampaign' }
  | { kind: 'entry' }
  | { kind: 'smsPhone' }
  | { kind: 'smsCode'; challenge: OtpIssued; phone: string }
  | { kind: 'voucher'; pendingCard: VoucherCard | null; autoSubmit: boolean }
  | { kind: 'account' }
  | { kind: 'adWatch'; campaign: AdCampaign; sessionId: string }
  | { kind: 'success'; result: RouterLogin }
  | { kind: 'accountActivated'; login: string; activated: ActivatedPackage }
  | { kind: 'error'; message: string }

/**
 * Boshlang'ich holat BIRINCHI chizishdayoq aniq bo'lishi shart: skanerlangan
 * karta `VoucherStep` o'rnatilishi bilan yuboriladi va o'sha paytda
 * kampaniya tanlangan bo'lishi kerak (bola effekti ota effektidan oldin yuradi).
 */
function initialCampaignId(zone: ZoneOptions): string | null {
  return zone.adRequired && zone.campaigns.length === 1 ? (zone.campaigns[0]?.id ?? null) : null
}

function initialStep(zone: ZoneOptions, pendingCard: VoucherCard | null): Step {
  if (zone.adRequired && zone.campaigns.length === 0) return { kind: 'blockedNoAds' }
  if (zone.adRequired && zone.campaigns.length > 1) return { kind: 'chooseCampaign' }
  return pendingCard ? { kind: 'voucher', pendingCard, autoSubmit: true } : { kind: 'entry' }
}

/**
 * Bosh oqim — zona ma'lumoti YUKLANGANDAN keyin ishga tushadi.
 *
 * ⚠️ Reklama kampaniyasi TANLASH IDENTIFIKATSIYADAN OLDIN sodir bo'ladi,
 * ekranlar ro'yxatida keyinroq tursa ham: backend kontrakti buni talab
 * qiladi (`GuestAccessServiceImpl.resolveCampaign` — agar zona
 * `ADVERTISER` bilan moliyalashtirilsa va `campaignId` so'rovda YO'Q
 * bo'lsa, `GuestAccessUnavailable` bilan rad etadi). Ya'ni kampaniyani
 * "reklama ko'rsatish" bosqichida emas, identifikatsiya so'rovi bilan
 * BIRGA yuborish kerak — shu sababli tanlov Kirish ekranidan OLDIN.
 */
function PortalFlow({ params, zone }: { params: PortalParams; zone: ZoneOptions }) {
  const { t } = useTranslation()
  const userAgent = useMemo(() => navigator.userAgent.slice(0, 512), [])
  const [pendingCard, setPendingCard] = useState(() =>
    zone.allowedMethods.includes('VOUCHER') ? readPendingVoucher(sessionStore()) : null,
  )
  const [campaignId, setCampaignId] = useState<string | null>(() => initialCampaignId(zone))
  const [step, setStep] = useState<Step>(() => initialStep(zone, pendingCard))

  const goEntry = (): void => setStep({ kind: 'entry' })

  const forgetPendingCard = (): void => {
    clearPendingVoucher(sessionStore())
    setPendingCard(null)
  }

  const handleAccessResult = (result: GuestAccessResult): void => {
    forgetPendingCard()
    if (hasAccess(result)) {
      setStep({ kind: 'success', result })
      return
    }
    // journalOnly — reklama bilan moliyalashtirilgan zona, kirish hali
    // yo'q (`GuestAccessResult.hasAccess()` javadoc'i).
    const campaign = zone.campaigns.find((c) => c.id === result.campaignId)
    if (!campaign) {
      setStep({ kind: 'error', message: t('ad.noCampaigns') })
      return
    }
    setStep({ kind: 'adWatch', campaign, sessionId: result.sessionId })
  }

  const handleAccountOutcome = (outcome: AccountVoucherOutcome, login: string): void => {
    forgetPendingCard()
    setStep(
      outcome.kind === 'routerLogin'
        ? { kind: 'success', result: outcome.login }
        : { kind: 'accountActivated', login, activated: outcome.activated },
    )
  }

  switch (step.kind) {
    case 'blockedNoAds':
      return <ErrorResult message={t('ad.noCampaigns')} />

    case 'chooseCampaign':
      return (
        <AdChooseStep
          campaigns={zone.campaigns}
          onChoose={(campaign) => {
            setCampaignId(campaign.id)
            setStep(pendingCard ? { kind: 'voucher', pendingCard, autoSubmit: true } : { kind: 'entry' })
          }}
        />
      )

    case 'entry':
      return (
        <EntryStep
          zone={zone}
          onSelect={(method: IdentificationMethod) => {
            if (method === 'SMS_OTP') setStep({ kind: 'smsPhone' })
            else if (method === 'VOUCHER') setStep({ kind: 'voucher', pendingCard, autoSubmit: false })
          }}
          onAccount={() => setStep({ kind: 'account' })}
        />
      )

    case 'smsPhone':
      return (
        <SmsPhoneStep
          zoneId={zone.zoneId}
          mac={params.mac}
          ip={params.ip}
          onIssued={(challenge, phone) => setStep({ kind: 'smsCode', challenge, phone })}
          onBack={goEntry}
        />
      )

    case 'smsCode':
      return (
        <SmsCodeStep
          zoneId={zone.zoneId}
          mac={params.mac}
          ip={params.ip}
          nasIdentifier={params.nasIdentifier}
          userAgent={userAgent}
          challenge={step.challenge}
          phone={step.phone}
          campaignId={campaignId}
          onAccess={handleAccessResult}
          onChangeNumber={() => setStep({ kind: 'smsPhone' })}
        />
      )

    case 'voucher':
      return (
        <VoucherStep
          zoneId={zone.zoneId}
          mac={params.mac}
          ip={params.ip}
          nasIdentifier={params.nasIdentifier}
          userAgent={userAgent}
          campaignId={campaignId}
          pendingCard={step.pendingCard}
          autoSubmit={step.autoSubmit}
          onAccess={handleAccessResult}
          onCardRejected={forgetPendingCard}
          onBack={goEntry}
        />
      )

    case 'account':
      return (
        <AccountVoucherStep
          zoneId={zone.zoneId}
          mac={params.mac}
          ip={params.ip}
          nasIdentifier={params.nasIdentifier}
          onRedeemed={handleAccountOutcome}
          onBack={goEntry}
        />
      )

    case 'adWatch':
      return (
        <AdWatchStep
          sessionId={step.sessionId}
          campaign={step.campaign}
          onAccess={handleAccessResult}
          onFatalError={(message) => setStep({ kind: 'error', message })}
        />
      )

    case 'success':
      // ⚠️ `loginUrl` hujjatlashtirilmagan bog'liqlik
      // (`shared/lib/portalParams.ts`). Yo'q bo'lsa — hech qayerga
      // yubormaydigan "muvaffaqiyat" ko'rsatishdan ko'ra aniq xato.
      if (!params.loginUrl) {
        return <ErrorResult message={t('error.loginTargetMissing')} />
      }
      return <SuccessStep result={step.result} loginUrl={params.loginUrl} dst={params.orig} chap={params.chap} />

    case 'accountActivated':
      return <AccountActivatedStep login={step.login} activated={step.activated} />

    case 'error':
      return <ErrorResult message={step.message} onStartOver={goEntry} />

    default: {
      const exhaustive: never = step
      return exhaustive
    }
  }
}

export function PortalPage() {
  const { t } = useTranslation()
  const location = useLocation()

  const params = useMemo(() => parsePortalParams(location.search), [location.search])
  const nasIdentifier = params?.nasIdentifier ?? null

  const zoneQuery = useQuery({
    queryKey: ['portal', 'zone', nasIdentifier],
    queryFn: () => fetchZoneOptions(nasIdentifier ?? ''),
    enabled: nasIdentifier !== null,
  })

  if (!params || !looksLikeMac(params.mac)) {
    return <ConfigErrorPage />
  }

  if (zoneQuery.isPending) {
    return (
      <PortalShell pending>
        <LoadingScreen text={t('common.loading')} />
      </PortalShell>
    )
  }

  if (zoneQuery.isError) {
    return (
      <PortalShell>
        <ErrorResult
          title={t('entry.loadFailed')}
          message={translatePortalError(zoneQuery.error, t).message}
          onRetry={() => void zoneQuery.refetch()}
          retryLoading={zoneQuery.isFetching}
        />
      </PortalShell>
    )
  }

  const zone = zoneQuery.data

  return (
    <PortalShell venueName={zone.venueName} zoneName={zone.zoneName} branding={zone.branding}>
      <PortalFlow params={params} zone={zone} />
    </PortalShell>
  )
}
